/**
 * The org's saved tokens. They live in the settings, org scope: a brand is
 * decided for the whole call centre, not per browser. A value kept in local
 * storage would give each workstation a different colour, and a supervisor's
 * screenshot would no longer show what the agent sees.
 */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { session } from '@/lib/auth'
import { SETTING_KEY, fromSaved, type TokensByTheme } from './tokens'

export const SETTING_QUERY_KEY = ['setting', SETTING_KEY] as const

export function useSavedTokens() {
  const q = useQuery({
    queryKey: SETTING_QUERY_KEY,
    queryFn: () => api<{ key: string; value: unknown }>(`/settings/${SETTING_KEY}`),
    // The brand does not move during a working session: reading it again at
    // every return to the tab would make the interface flicker.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Wherever it is mounted, never asked for before an org session exists.
    enabled: computed(() => Boolean(session.user)),
  })
  /** As Vue token names (a value saved by the React app included). */
  const tokens = computed<TokensByTheme>(() => fromSaved(q.data.value?.value ?? null))
  return { query: q, tokens }
}
